import React from 'react';
import PropTypes from 'prop-types';
import { Box, Typography } from '@mui/material';

/**
 * Predefined styling for hint types.
 * Each type maps to an emoji icon and background color.
 */
const HINT_STYLES = {
  coaching: { icon: '🎯', bg: '#F4F6F8', labelColor: '#212B36' },
  empathy: { icon: '💚', bg: '#F4F6F8', labelColor: '#212B36' },
  sales: { icon: '🎯', bg: '#F4F6F8', labelColor: '#212B36' },
  tip: { icon: '💡', bg: '#FFF7CD', labelColor: '#7A4F01' },
  warning: { icon: '⚠️', bg: '#FFF2E2', labelColor: '#B76E00' },
};

const HintRow = ({ type, text, icon }) => {
  const style = HINT_STYLES[type] || HINT_STYLES.coaching;
  const displayIcon = icon || style.icon;

  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'flex-start',
        gap: '10px',
        backgroundColor: style.bg,
        borderRadius: '10px',
        px: '14px',
        py: '10px',
        mb: '6px',
      }}
    >
      <Box
        sx={{
          width: 24,
          height: 24,
          borderRadius: '50%',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          flexShrink: 0,
          fontSize: '14px',
          mt: '1px',
        }}
      >
        {displayIcon}
      </Box>
      <Typography sx={{ fontSize: '0.82rem', color: '#454F5B', lineHeight: 1.5 }}>
        <Typography
          component="span"
          sx={{ fontWeight: 700, color: style.labelColor, fontSize: '0.82rem' }}
        >
          {type.charAt(0).toUpperCase() + type.slice(1)}:
        </Typography>
        {' '}{text}
      </Typography>
    </Box>
  );
};

HintRow.propTypes = {
  type: PropTypes.string.isRequired,
  text: PropTypes.string.isRequired,
  icon: PropTypes.string,
};

HintRow.defaultProps = {
  icon: '',
};

/**
 * Renders coaching hint block above card content.
 * Shown when metadata.coaching exists.
 */
export const CoachingBlock = ({ text, icon }) => {
  if (!text) return null;
  return (
    <Box sx={{ mb: '10px' }}>
      <HintRow type="coaching" text={text} icon={icon} />
    </Box>
  );
};

CoachingBlock.propTypes = {
  text: PropTypes.string,
  icon: PropTypes.string,
};

CoachingBlock.defaultProps = {
  text: '',
  icon: '',
};

/**
 * Renders hint blocks below card content (empathy, sales, etc.).
 * Accepts an object like { empathy: "text", sales: "text" }.
 * Excludes 'coaching' (rendered above content) and 'followUp' (rendered by CloseCall).
 */
export const HintBlocks = ({ hints }) => {
  if (!hints || typeof hints !== 'object') return null;
  const entries = Object.entries(hints).filter(([key]) => key !== 'coaching' && key !== 'followUp');
  if (entries.length === 0) return null;
  return (
    <Box
      sx={{
        mt: '12px',
        pt: '12px',
        borderTop: '1px dashed rgba(145, 158, 171, 0.3)',
      }}
    >
      {entries.map(([type, text]) => (
        <HintRow
          key={type}
          type={type}
          text={text}
        />
      ))}
    </Box>
  );
};

HintBlocks.propTypes = {
  hints: PropTypes.object,
};

HintBlocks.defaultProps = {
  hints: null,
};
